import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { usePageTitle } from '../../hooks/usePageTitle'
import { supabase } from '../../lib/supabase'
import { useSecurity } from '../../hooks/useSecurity'
import { validatePassword, getPasswordErrorMessage } from '../../utils/passwordValidation'
import {
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Input,
  Badge,
  Animate,
  Modal,
  Skeleton,
  Spinner,
  OTPInput,
  CopyButton,
  Checkbox,
  Tabs,
  TabPanels,
  TabPanel,
  useToast
} from 'wss3-forge'
import {
  Key20Regular,
  ShieldCheckmark20Regular,
  Eye20Regular,
  EyeOff20Regular,
  Checkmark20Regular,
  ArrowDownload20Regular,
  ArrowSync20Regular,
  Fingerprint20Regular,
  Person20Regular
} from '@fluentui/react-icons'
import {
  PasskeysCard,
  AccountManagementCard
} from '../../components/security'

interface MFAFactor {
  id: string
  factor_type: string
  status: string
  created_at: string
}

export function Security() {
  const { user } = useAuth()
  const toast = useToast()
  const security = useSecurity()
  usePageTitle('Security')

  // Password state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [logoutEverywhere, setLogoutEverywhere] = useState(false)

  // 2FA state
  const [mfaFactors, setMfaFactors] = useState<MFAFactor[]>([])
  const [loadingMFA, setLoadingMFA] = useState(true)
  const [showSetupModal, setShowSetupModal] = useState(false)
  const [setupStep, setSetupStep] = useState<'qr' | 'verify'>('qr')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [factorId, setFactorId] = useState('')
  const [verifyCode, setVerifyCode] = useState('')
  const [enrolling, setEnrolling] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [disabling, setDisabling] = useState(false)

  // Recovery codes state
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [showRecoveryModal, setShowRecoveryModal] = useState(false)
  const [remainingRecoveryCodes, setRemainingRecoveryCodes] = useState(0)
  const [regeneratingCodes, setRegeneratingCodes] = useState(false)

  // Tab state
  const [activeTab, setActiveTab] = useState('authentication')
  const mfaLoadDone = useRef(false)

  const twoFactorEnabled = mfaFactors.some(f => f.status === 'verified')

  useEffect(() => {
    if (!mfaLoadDone.current) {
      loadMFAFactors()
    }
  }, [])

  const loadMFAFactors = async (force = false) => {
    if (mfaLoadDone.current && !force) return

    try {
      const { data, error } = await supabase.auth.mfa.listFactors()
      if (error) throw error
      setMfaFactors(data?.totp || [])
      mfaLoadDone.current = true
      setLoadingMFA(false)

      // Load remaining recovery codes count in background if MFA is enabled
      if (data?.totp?.some(f => f.status === 'verified') && user) {
        supabase.rpc('has_mfa_recovery_codes', {
          p_user_id: user.id
        }).then(({ data: count }) => {
          setRemainingRecoveryCodes(count || 0)
        })
      }
    } catch (err) {
      console.error('Error loading MFA factors:', err)
      setLoadingMFA(false)
    }
  }

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields')
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match')
      return
    }

    const passwordValidation = validatePassword(newPassword)
    if (!passwordValidation.isValid) {
      toast.error(getPasswordErrorMessage(passwordValidation.errors))
      return
    }

    setChangingPassword(true)

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error

      // Record password change
      await supabase.rpc('record_password_change', {
        p_user_id: user?.id,
        p_user_agent: navigator.userAgent,
        p_invalidate_sessions: logoutEverywhere
      })

      // If logout everywhere is selected, sign out all other sessions
      if (logoutEverywhere) {
        await supabase.auth.signOut({ scope: 'others' })
      }

      toast.success(logoutEverywhere
        ? 'Password updated and all other sessions have been logged out'
        : 'Password updated successfully'
      )
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setLogoutEverywhere(false)

      // Reload security data to show the notification
      security.reload()
    } catch (err: any) {
      console.error('Error changing password:', err)
      toast.error(err.message || 'Failed to update password')
    } finally {
      setChangingPassword(false)
    }
  }

  const handleStartSetup = async () => {
    setEnrolling(true)
    setShowSetupModal(true)
    setSetupStep('qr')
    setVerifyCode('')

    try {
      // Clean up any existing unverified factors first
      const { data: existingFactors } = await supabase.auth.mfa.listFactors()
      const allFactors = existingFactors?.all || []

      for (const factor of allFactors) {
        if (factor.factor_type === 'totp' && factor.status !== 'verified') {
          await supabase.auth.mfa.unenroll({ factorId: factor.id })
        }
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        issuer: 'Webba iD'
      })

      if (error) throw error

      if (data?.totp) {
        setQrCode(data.totp.qr_code)
        setSecret(data.totp.secret)
        setFactorId(data.id)
      }
    } catch (err: any) {
      console.error('Error enrolling MFA:', err)
      toast.error(err.message || 'Failed to start 2FA setup')
      setShowSetupModal(false)
    } finally {
      setEnrolling(false)
    }
  }

  const handleVerifySetup = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code')
      return
    }

    setVerifying(true)

    try {
      // Challenge the factor
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId
      })

      if (challengeError) throw challengeError

      // Verify with the code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode
      })

      if (verifyError) throw verifyError

      // Generate recovery codes
      if (user) {
        const { data: codes, error: codesError } = await supabase.rpc('generate_mfa_recovery_codes', {
          p_user_id: user.id
        })

        if (!codesError && codes) {
          setRecoveryCodes(codes)
          setShowSetupModal(false)
          setShowRecoveryModal(true)
        }
      }

      toast.success('Two-factor authentication enabled!')
      await loadMFAFactors(true)

      // Create security notification for MFA enabled
      if (user) {
        await supabase.rpc('create_security_notification', {
          p_user_id: user.id,
          p_type: 'mfa_enabled',
          p_title: 'Two-factor authentication enabled',
          p_message: 'Two-factor authentication has been enabled on your account.',
          p_metadata: {}
        })
        security.reload()
      }
    } catch (err: any) {
      console.error('Error verifying MFA:', err)
      toast.error(err.message || 'Invalid verification code')
    } finally {
      setVerifying(false)
    }
  }

  const handleDisable2FA = async () => {
    const verifiedFactor = mfaFactors.find(f => f.status === 'verified')
    if (!verifiedFactor) return

    if (!confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) return

    setDisabling(true)

    try {
      const { error } = await supabase.auth.mfa.unenroll({
        factorId: verifiedFactor.id
      })

      if (error) throw error

      toast.success('Two-factor authentication disabled')
      await loadMFAFactors(true)

      // Create security notification for MFA disabled
      if (user) {
        await supabase.rpc('create_security_notification', {
          p_user_id: user.id,
          p_type: 'mfa_disabled',
          p_title: 'Two-factor authentication disabled',
          p_message: 'Two-factor authentication has been disabled on your account.',
          p_metadata: {}
        })
        security.reload()
      }
    } catch (err: any) {
      console.error('Error disabling MFA:', err)
      toast.error(err.message || 'Failed to disable 2FA')
    } finally {
      setDisabling(false)
    }
  }

  const handleCloseModal = () => {
    setShowSetupModal(false)
    setQrCode('')
    setSecret('')
    setFactorId('')
    setVerifyCode('')
    setSetupStep('qr')
  }

  const handleRegenerateRecoveryCodes = async () => {
    if (!user) return

    setRegeneratingCodes(true)

    try {
      const { data: codes, error } = await supabase.rpc('generate_mfa_recovery_codes', {
        p_user_id: user.id
      })

      if (error) throw error

      setRecoveryCodes(codes)
      setShowRecoveryModal(true)
      setRemainingRecoveryCodes(8)
      toast.success('New recovery codes generated!')
    } catch (err: any) {
      console.error('Error regenerating codes:', err)
      toast.error(err.message || 'Failed to generate recovery codes')
    } finally {
      setRegeneratingCodes(false)
    }
  }

  const handleDownloadRecoveryCodes = () => {
    const content = `Webba ID Recovery Codes
========================
Generated: ${new Date().toLocaleDateString()}

Keep these codes in a safe place. Each code can only be used once.

${recoveryCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

If you lose access to your authenticator app, you can use one of these codes to sign in.
`
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'webba-id-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  if (loadingMFA) {
    return (
      <div className="page-content">
        <VStack gap="md">
          <VStack gap="xs">
            <Skeleton width={120} height={32} />
            <Skeleton width={360} height={20} />
          </VStack>
          <Skeleton width="100%" height={48} />
          <Card padding="md">
            <VStack gap="md">
              <Skeleton width={180} height={24} />
              <Skeleton width="100%" height={56} />
              <Skeleton width="100%" height={56} />
              <Skeleton width="100%" height={56} />
            </VStack>
          </Card>
          <Card padding="md">
            <VStack gap="md">
              <Skeleton width={200} height={24} />
              <Skeleton width="100%" height={80} />
            </VStack>
          </Card>
        </VStack>
      </div>
    )
  }

  return (
    <div className="page-content">
      <VStack gap="md">
        <Animate type="fadeIn">
          <VStack gap="none">
            <Heading level={2}>Security</Heading>
            <Text color="muted">Manage your password, authentication, and account security</Text>
          </VStack>
        </Animate>

        <Animate type="slideInUp" delay={50}>
          <Tabs
            tabs={[
              { id: 'authentication', label: 'Authentication', icon: <Key20Regular /> },
              { id: 'passkeys', label: 'Passkeys', icon: <Fingerprint20Regular /> },
              { id: 'account', label: 'Account', icon: <Person20Regular /> }
            ]}
            active={activeTab}
            onChange={setActiveTab}
            stretchLine
          />
        </Animate>

        <TabPanels active={activeTab}>
          <TabPanel id="authentication" active={activeTab}>
            <VStack gap="md">
              {/* Change Password */}
              <Card padding="md">
                <VStack gap="md">
                  <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                    <Key20Regular style={{ color: 'var(--brand-primary)' }} />
                    <Heading level={5}>Change Password</Heading>
                  </HStack>

                  <VStack gap="sm">
                    <div style={{ position: 'relative' }}>
                      <Input
                        label="Current password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(value) => setCurrentPassword(value as string)}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '34px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-muted)',
                          padding: '4px'
                        }}
                      >
                        {showCurrentPassword ? <EyeOff20Regular /> : <Eye20Regular />}
                      </button>
                    </div>

                    <div style={{ position: 'relative' }}>
                      <Input
                        label="New password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(value) => setNewPassword(value as string)}
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '34px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-muted)',
                          padding: '4px'
                        }}
                      >
                        {showNewPassword ? <EyeOff20Regular /> : <Eye20Regular />}
                      </button>
                    </div>

                    <Input
                      label="Confirm new password"
                      type="password"
                      value={confirmPassword}
                      onChange={(value) => setConfirmPassword(value as string)}
                      placeholder="Confirm new password"
                    />

                    <Text size="sm" color="muted">
                      Password must have 8+ characters, uppercase, lowercase, number, and special character
                    </Text>

                  </VStack>

                  <div style={{ marginTop: '0.5rem' }}>
                    <Checkbox
                      checked={logoutEverywhere}
                      onChange={setLogoutEverywhere}
                      label="Log out of all other devices"
                    />
                  </div>

                  <HStack gap="sm" style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                    <Button
                      onClick={handleChangePassword}
                      loading={changingPassword}
                      disabled={changingPassword || !newPassword || !confirmPassword}
                    >
                      Update password
                    </Button>
                  </HStack>
                </VStack>
              </Card>

              {/* Two-Factor Authentication */}
              <Card padding="md">
                <VStack gap="md">
                  <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                    <ShieldCheckmark20Regular style={{ color: 'var(--brand-primary)' }} />
                    <Heading level={5}>Two-Factor Authentication</Heading>
                  </HStack>

                  {loadingMFA ? (
                    <VStack gap="sm">
                      <Skeleton width={120} height={24} />
                      <Skeleton width="100%" height={40} />
                      <Skeleton width={100} height={36} />
                    </VStack>
                  ) : (
                    <VStack gap="sm">
                      <HStack gap="sm" style={{ alignItems: 'center' }}>
                        <Text>Status:</Text>
                        <Badge variant={twoFactorEnabled ? 'success' : 'default'}>
                          {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </HStack>

                      <Text size="sm" color="muted">
                        Add an extra layer of security to your account by requiring a verification code from your authenticator app when signing in.
                      </Text>

                      {twoFactorEnabled ? (
                        <>
                          <Card variant="outlined" padding="sm">
                            <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                              <VStack gap="xs">
                                <Text size="sm" weight="medium">Recovery Codes</Text>
                                <Text size="xs" color="muted">
                                  {remainingRecoveryCodes} codes remaining
                                </Text>
                              </VStack>
                              <Button
                                variant="secondary"
                                size="sm"
                                icon={<ArrowSync20Regular />}
                                onClick={handleRegenerateRecoveryCodes}
                                loading={regeneratingCodes}
                              >
                                Regenerate
                              </Button>
                            </HStack>
                          </Card>
                          <HStack gap="sm" style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                            <Button
                              variant="danger"
                              onClick={handleDisable2FA}
                              loading={disabling}
                              disabled={disabling}
                            >
                              Disable 2FA
                            </Button>
                          </HStack>
                        </>
                      ) : (
                        <HStack gap="sm" style={{ justifyContent: 'flex-end' }}>
                          <Button
                            variant="primary"
                            onClick={handleStartSetup}
                          >
                            Enable 2FA
                          </Button>
                        </HStack>
                      )}
                    </VStack>
                  )}
                </VStack>
              </Card>
            </VStack>
          </TabPanel>

          <TabPanel id="passkeys" active={activeTab}>
            <PasskeysCard
              passkeys={security.passkeys}
              loading={security.loading}
              onDelete={security.deletePasskey}
              onRename={security.renamePasskey}
              onReload={security.reload}
            />
          </TabPanel>

          <TabPanel id="account" active={activeTab}>
            <AccountManagementCard
              deletionRequest={security.deletionRequest}
              loading={security.loading}
              onExportData={security.exportUserData}
              onRequestDeletion={security.requestAccountDeletion}
              onCancelDeletion={security.cancelAccountDeletion}
            />
          </TabPanel>
        </TabPanels>
      </VStack>

      {/* 2FA Setup Modal */}
      <Modal
        open={showSetupModal}
        onClose={handleCloseModal}
        title="Set up Two-Factor Authentication"
      >
        <VStack gap="md">
          {enrolling ? (
            <VStack gap="sm" style={{ alignItems: 'center', padding: '2rem' }}>
              <Spinner size="lg" />
              <Text>Generating QR code...</Text>
            </VStack>
          ) : setupStep === 'qr' ? (
            <>
              <Text>
                Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
              </Text>

              {qrCode && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '1rem',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  margin: '0 auto'
                }}>
                  <img
                    src={qrCode}
                    alt="2FA QR Code"
                    style={{ width: '200px', height: '200px' }}
                  />
                </div>
              )}

              <VStack gap="xs">
                <Text size="sm" color="muted">
                  Or enter this code manually:
                </Text>
                <code style={{
                  display: 'block',
                  padding: '0.75rem 1rem',
                  backgroundColor: 'var(--bg-tertiary)',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  wordBreak: 'break-all',
                  textAlign: 'center'
                }}>
                  {secret}
                </code>
              </VStack>

              <Button
                variant="primary"
                onClick={() => setSetupStep('verify')}
                style={{ width: '100%' }}
              >
                Continue
              </Button>
            </>
          ) : (
            <>
              <Text>
                Enter the 6-digit verification code from your authenticator app to complete the setup.
              </Text>

              <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
                <OTPInput
                  length={6}
                  value={verifyCode}
                  onChange={setVerifyCode}
                  onComplete={handleVerifySetup}
                />
              </div>

              <HStack gap="sm" style={{ justifyContent: 'flex-end' }}>
                <Button
                  variant="ghost"
                  onClick={() => setSetupStep('qr')}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleVerifySetup}
                  loading={verifying}
                  disabled={verifying || verifyCode.length !== 6}
                  icon={<Checkmark20Regular />}
                >
                  Verify & Enable
                </Button>
              </HStack>
            </>
          )}
        </VStack>
      </Modal>

      {/* Recovery Codes Modal */}
      <Modal
        open={showRecoveryModal}
        onClose={() => setShowRecoveryModal(false)}
        title="Save Your Recovery Codes"
      >
        <VStack gap="md">
          <Text>
            These codes can be used to access your account if you lose your authenticator device.
            Each code can only be used once.
          </Text>

          <Card variant="outlined" padding="md">
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.5rem'
            }}>
              {recoveryCodes.map((code, index) => (
                <HStack key={index} gap="sm" style={{ alignItems: 'center' }}>
                  <Text size="sm" color="muted" style={{ width: '1.5rem' }}>{index + 1}.</Text>
                  <code style={{
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: 'var(--bg-tertiary)',
                    borderRadius: '4px'
                  }}>
                    {code}
                  </code>
                </HStack>
              ))}
            </div>
          </Card>

          <Card variant="outlined" padding="sm" style={{ backgroundColor: 'var(--color-warning-bg)' }}>
            <Text size="sm">
              Store these codes in a secure location. You won't be able to see them again.
            </Text>
          </Card>

          <HStack gap="sm" style={{ justifyContent: 'flex-end' }}>
            <CopyButton text={recoveryCodes.join('\n')} label="Copy all" />
            <Button
              variant="secondary"
              icon={<ArrowDownload20Regular />}
              onClick={handleDownloadRecoveryCodes}
            >
              Download
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowRecoveryModal(false)}
            >
              I've saved my codes
            </Button>
          </HStack>
        </VStack>
      </Modal>
    </div>
  )
}
